/* eslint-disable import/no-unresolved */
import { Swiper, Navigation } from 'swiper';

const MARQUEE_SPEED = 2000;
const NAV_SPEED = 500;
const MARQUEE_DIRECTION_INTERVAL = 10000;
const USER_RESUME_DELAY = 3000;
const CLONE_ATTR = 'data-marquee-clone';

const instances = new WeakMap();

function getSlides(swiperEl, originalsOnly = false) {
  const slides = [...swiperEl.querySelectorAll('.swiper-slide')];

  return originalsOnly
    ? slides.filter((slide) => !slide.hasAttribute(CLONE_ATTR))
    : slides;
}

function ensureLoopSlides(swiperEl) {
  getSlides(swiperEl)
    .filter((slide) => slide.hasAttribute(CLONE_ATTR))
    .forEach((slide) => slide.remove());

  const originals = getSlides(swiperEl, true);
  if (!originals.length) {
    return null;
  }

  const slideWidth = originals[0].offsetWidth || 320;
  const visible = Math.max(1, Math.ceil((swiperEl.clientWidth || window.innerWidth) / slideWidth));
  const minSlides = Math.max(originals.length, visible * 2 + 2);
  const wrapper = swiperEl.querySelector('.swiper-wrapper');

  for (let i = originals.length; i < minSlides; i += 1) {
    const clone = originals[i % originals.length].cloneNode(true);
    clone.setAttribute(CLONE_ATTR, '');
    wrapper.appendChild(clone);
  }

  return {
    minSlides,
    loopedSlides: originals.length,
    loopAdditionalSlides: visible + 1,
  };
}

function setNavDisabled(btn, disabled) {
  btn.classList.toggle('disabled', disabled);
  // eslint-disable-next-line no-param-reassign
  btn.disabled = disabled;
  btn.setAttribute('aria-disabled', disabled ? 'true' : 'false');
}

function syncNav(swiper, prevEl, nextEl) {
  const disabled = swiper.params.loop
    ? [false, false]
    : [swiper.isBeginning, swiper.isEnd];

  setNavDisabled(prevEl, disabled[0]);
  setNavDisabled(nextEl, disabled[1]);
}

function createMarquee(swiper, hoverTarget, navButtons) {
  const state = {
    active: false,
    hoverPaused: false,
    userPaused: false,
    driven: false,
    direction: 1,
    reversePending: false,
    directionTimer: null,
    resumeTimer: null,
    abort: null,
    unbindSwiper: [],
  };

  const paused = () => state.hoverPaused || state.userPaused;

  const step = () => {
    if (!state.active || paused() || swiper.animating) {
      return;
    }

    if (state.reversePending) {
      state.direction *= -1;
      state.reversePending = false;
    }

    const { wrapperEl } = swiper;
    wrapperEl.style.transitionTimingFunction = 'linear';

    const advance = state.direction > 0 ? swiper.slideNext : swiper.slidePrev;
    state.driven = true;

    if (!advance.call(swiper, MARQUEE_SPEED) && swiper.loopFix) {
      swiper.loopFix();
      advance.call(swiper, MARQUEE_SPEED);
    }
  };

  const resumeLater = (delay = USER_RESUME_DELAY) => {
    clearTimeout(state.resumeTimer);
    state.resumeTimer = setTimeout(() => {
      state.userPaused = false;
      if (state.active && !paused() && !swiper.animating) {
        step();
      }
    }, delay);
  };

  const interrupt = (resumeDelay = USER_RESUME_DELAY) => {
    state.driven = false;
    state.userPaused = true;
    clearTimeout(state.resumeTimer);

    if (resumeDelay !== null) {
      resumeLater(resumeDelay);
    }
  };

  const onSwiper = (event, handler) => {
    swiper.on(event, handler);
    state.unbindSwiper.push(() => swiper.off(event, handler));
  };

  const stop = () => {
    state.active = false;
    state.hoverPaused = false;
    state.userPaused = false;
    state.driven = false;
    state.unbindSwiper.forEach((unbind) => unbind());
    state.unbindSwiper = [];
    clearInterval(state.directionTimer);
    clearTimeout(state.resumeTimer);
    state.directionTimer = null;
    state.resumeTimer = null;
    state.abort?.abort();
    state.abort = null;
  };

  const start = () => {
    stop();
    state.active = true;

    onSwiper('slideChangeTransitionEnd', () => {
      if (state.driven) {
        state.driven = false;
        step();
      }
    });

    onSwiper('touchStart', () => interrupt(null));
    onSwiper('touchEnd', () => resumeLater());

    state.abort = new AbortController();
    const { signal } = state.abort;

    hoverTarget.addEventListener('mouseenter', () => {
      state.hoverPaused = true;
    }, { signal });

    hoverTarget.addEventListener('mouseleave', () => {
      state.hoverPaused = false;
      if (!swiper.animating && !state.userPaused) {
        step();
      }
    }, { signal });

    navButtons.forEach((btn) => {
      btn.addEventListener('click', () => interrupt(), { signal });
    });

    state.directionTimer = setInterval(() => {
      if (!state.active || state.reversePending) {
        return;
      }

      state.reversePending = true;
      if (!swiper.animating && !paused()) {
        step();
      }
    }, MARQUEE_DIRECTION_INTERVAL);

    step();
  };

  return { start, stop, isActive: () => state.active };
}

function destroyInstance(rootEl) {
  const instance = instances.get(rootEl);
  instance?.marquee.stop();
  instance?.observer.disconnect();
  instances.delete(rootEl);
}

function initSocialGalleryRoot(rootEl) {
  const swiperEl = rootEl.querySelector('.social-gallery__swiper');
  const navContainer = swiperEl?.closest('.social-gallery__carousel');
  const prevEl = navContainer?.querySelector('.swiper-nav__button--prev');
  const nextEl = navContainer?.querySelector('.swiper-nav__button--next');

  if (!swiperEl || !prevEl || !nextEl) {
    return;
  }

  const originals = getSlides(swiperEl, true);
  if (originals.length <= 1) {
    setNavDisabled(prevEl, true);
    setNavDisabled(nextEl, true);
    return;
  }

  destroyInstance(rootEl);
  swiperEl.swiper?.destroy(true, true);

  const loop = ensureLoopSlides(swiperEl);
  if (!loop) {
    return;
  }

  const updateNav = (swiper) => syncNav(swiper, prevEl, nextEl);
  let resizeTimer;

  const swiper = new Swiper(swiperEl, {
    modules: [Navigation],
    slidesPerView: 'auto',
    loop: true,
    loopedSlides: loop.loopedSlides,
    loopAdditionalSlides: loop.loopAdditionalSlides,
    speed: NAV_SPEED,
    grabCursor: true,
    watchOverflow: false,
    observer: true,
    observeParents: true,
    navigation: { prevEl, nextEl },
    on: {
      init: updateNav,
      slideChange: updateNav,
      resize(swiperInstance) {
        updateNav(swiperInstance);
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          if (getSlides(swiperEl).length < loop.minSlides) {
            initSocialGalleryRoot(rootEl);
          }
        }, 200);
      },
    },
  });

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return;
  }

  const marquee = createMarquee(swiper, navContainer, [prevEl, nextEl]);
  const observer = new IntersectionObserver(
    ([entry]) => (entry.isIntersecting ? marquee.start() : marquee.stop()),
    { threshold: 0.25 },
  );

  observer.observe(rootEl);
  instances.set(rootEl, { marquee, observer });
}

function initAllSocialGalleries() {
  document.querySelectorAll('.social-gallery').forEach(initSocialGalleryRoot);
}

document.addEventListener('DOMContentLoaded', () => {
  initAllSocialGalleries();

  if (window.acf) {
    window.acf.addAction('render_block_preview/type=azds-social-gallery', initAllSocialGalleries);
  }
});
